import {
  getLatestProjectMeasurements,
  getProjectMeasurements,
  getProjectSensors,
} from "helpers/apiClient";
import { sensorCoordinates } from "data/sensor_data";

const legacyMetrics = [
  { metricCode: "pressure", displayName: "pressure", unit: "bar" },
  { metricCode: "temperature", displayName: "temperature", unit: "°C" },
  { metricCode: "flow", displayName: "flow", unit: "m³/s" },
];

const responseItems = (response) => {
  if (Array.isArray(response)) return response;
  return Array.isArray(response?.items) ? response.items : [];
};

const measurementValue = (value) => {
  if (value === null || value === undefined) return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const metadataValue = (metadata, key) =>
  metadata?.location?.[key] ?? metadata?.[key];

const validCoordinates = (coordinates) =>
  Array.isArray(coordinates) && coordinates.length >= 2
    ? coordinates.slice(0, 2)
    : null;

const metricDefinitions = (sensor, measurements) => {
  const definitions = new Map();

  (sensor?.metrics || []).forEach((metric) => {
    if (!metric?.metricCode) return;
    definitions.set(metric.metricCode, {
      metricCode: metric.metricCode,
      displayName: metric.displayName || metric.metricCode,
      unit: metric.unit || definitions.get(metric.metricCode)?.unit || "",
    });
  });

  measurements.forEach((measurement) => {
    if (!measurement?.metricCode) return;
    const existing = definitions.get(measurement.metricCode) || {};
    definitions.set(measurement.metricCode, {
      metricCode: measurement.metricCode,
      displayName:
        measurement.metricDisplayName ||
        existing.displayName ||
        measurement.metricCode,
      unit: measurement.unit || existing.unit || "",
    });
  });

  // Older locally stored sensor shapes did not describe their metrics. Keep
  // that compatibility fallback, but never add fake metrics to a backend
  // Sensor that already supplies its authoritative metric catalogue.
  if (definitions.size === 0) {
    legacyMetrics.forEach((metric) =>
      definitions.set(metric.metricCode, metric)
    );
  }

  return Array.from(definitions.values());
};

export const toLegacySensor = (sensor, measurements = []) => {
  const measurementItems = Array.isArray(measurements) ? measurements : [];
  const externalId = String(sensor?.externalId ?? sensor?.id ?? "");
  const coordinatesFromCatalog = sensorCoordinates[externalId];
  const metadata = sensor?.metadata || {};
  const sensorMeasurements = measurementItems.filter(
    (measurement) =>
      measurement?.sensorId === sensor?.id ||
      String(measurement?.externalId ?? "") === externalId
  );
  const coordinates =
    validCoordinates(metadataValue(metadata, "coordinates")) ||
    (coordinatesFromCatalog
      ? [coordinatesFromCatalog.coordx, coordinatesFromCatalog.coordy]
      : [0, 0]);
  const coordinates3d =
    validCoordinates(metadataValue(metadata, "coordinates3d")) ||
    (coordinatesFromCatalog
      ? [coordinatesFromCatalog.coordx3d, coordinatesFromCatalog.coordy3d]
      : [0, 0]);

  return {
    ...sensor,
    id: sensor?.id,
    deviceId: externalId,
    title:
      sensor?.name ||
      coordinatesFromCatalog?.title ||
      `Sensor ${externalId}`,
    notifications: [],
    location: {
      city: metadataValue(metadata, "city") ?? "Valencia",
      specificLocation:
        metadataValue(metadata, "specificLocation") ?? "Marjals i Extremals",
      coordinates,
      coordinates3d,
    },
    timeFrame: {
      start: "",
      end: "",
    },
    data: metricDefinitions(sensor, sensorMeasurements).map((metric) => ({
      category: metric.metricCode,
      displayName: metric.displayName,
      unit: metric.unit,
      measurements: sensorMeasurements
        .filter((measurement) => measurement.metricCode === metric.metricCode)
        .map((measurement) => ({
          measurementId: measurement.measurementId,
          date: new Date(measurement.recordedAt),
          value: measurementValue(measurement.value),
          unit: measurement.unit || metric.unit,
          quality: measurement.quality ?? null,
        }))
        .filter(
          (measurement) =>
            !Number.isNaN(measurement.date.getTime()) &&
            measurement.value !== null
        )
        .sort((a, b) => a.date - b.date),
    })),
  };
};

export const transformProjectSensorData = ({ sensors = [], measurements = [] } = {}) =>
  (Array.isArray(sensors) ? sensors : []).map((sensor) =>
    toLegacySensor(sensor, measurements)
  );

export const mergeLatestSensorData = (
  currentSensors = [],
  latestSensors = [],
  maxMeasurements = 1000
) => {
  if (!Array.isArray(currentSensors) || currentSensors.length === 0) {
    return Array.isArray(latestSensors) ? latestSensors : [];
  }

  return currentSensors.map((sensor) => {
    const latestSensor = (Array.isArray(latestSensors) ? latestSensors : []).find(
      (candidate) =>
        candidate?.id === sensor?.id ||
        candidate?.deviceId === sensor?.deviceId
    );
    if (!latestSensor) return sensor;

    const data = sensor.data.map((metric) => {
      const latestMetric = latestSensor.data.find(
        (candidate) => candidate.category === metric.category
      );
      if (!latestMetric) return metric;

      const measurements = new Map();
      [...metric.measurements, ...latestMetric.measurements].forEach(
        (measurement) => {
          const timestamp = new Date(measurement.date).getTime();
          if (!Number.isFinite(timestamp)) return;
          const key = measurement.measurementId || String(timestamp);
          measurements.set(key, measurement);
        }
      );

      return {
        ...metric,
        ...latestMetric,
        measurements: Array.from(measurements.values())
          .sort((left, right) => new Date(left.date) - new Date(right.date))
          .slice(-maxMeasurements),
      };
    });

    return { ...sensor, ...latestSensor, data };
  });
};

export const loadProjectSensors = async (projectId, filters = {}) => {
  if (!projectId) return [];
  return responseItems(await getProjectSensors(projectId, filters));
};

export const loadProjectMeasurements = async (projectId, filters = {}) => {
  if (!projectId) return { items: [], nextCursor: null };
  const response = await getProjectMeasurements(projectId, filters);
  return {
    items: responseItems(response),
    nextCursor: response?.nextCursor || null,
  };
};

export const loadLatestProjectMeasurements = async (projectId, filters = {}) => {
  if (!projectId) return [];
  return responseItems(await getLatestProjectMeasurements(projectId, filters));
};

export const loadProjectMeasurementHistory = async (
  projectId,
  filters = {},
  maxMeasurements = 200
) => {
  if (!projectId || maxMeasurements <= 0) return [];

  const measurements = [];
  let cursor = filters.cursor;

  while (measurements.length < maxMeasurements) {
    const limit = Math.min(100, maxMeasurements - measurements.length);
    const page = await loadProjectMeasurements(projectId, {
      ...filters,
      cursor,
      limit,
    });
    measurements.push(...page.items);
    if (!page.nextCursor || page.items.length === 0) break;
    cursor = page.nextCursor;
  }

  return measurements.slice(0, maxMeasurements);
};

export const loadProjectMeasurementRange = async (projectId, filters = {}) => {
  if (!projectId || !filters.from || !filters.to) return [];

  const measurements = [];
  const visitedCursors = new Set();
  let cursor = filters.cursor;

  while (true) {
    const page = await loadProjectMeasurements(projectId, {
      ...filters,
      cursor,
      limit: 100,
    });
    measurements.push(...page.items);

    if (
      !page.nextCursor ||
      page.items.length === 0 ||
      visitedCursors.has(page.nextCursor)
    ) {
      break;
    }
    visitedCursors.add(page.nextCursor);
    cursor = page.nextCursor;
  }

  return measurements;
};

export const loadProjectSensorData = async (
  projectId,
  { latest = false, sensorFilters = {}, measurementFilters = {} } = {}
) => {
  if (!projectId) return { sensors: [], measurements: [] };

  const [sensors, measurements] = await Promise.all([
    loadProjectSensors(projectId, {
      includeGeometry: true,
      ...sensorFilters,
    }),
    latest
      ? loadLatestProjectMeasurements(projectId, measurementFilters)
      : loadProjectMeasurementHistory(projectId, measurementFilters),
  ]);
  return { sensors, measurements };
};

export const startSensorPolling = (
  enabled,
  fetchInitialHistory,
  fetchLatestMeasurements,
  { setIntervalFn = setInterval, clearIntervalFn = clearInterval } = {}
) => {
  if (!enabled) return () => {};

  fetchInitialHistory();
  const intervalId = setIntervalFn(fetchLatestMeasurements, 10000);
  return () => clearIntervalFn(intervalId);
};
